import { MAX_DATE } from "@utils/constants";
import { formatDate } from "@utils/utils";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import {
  Headline,
  Input,
  Paragraph,
  Select,
  SelectItem,
  Small,
  TextArea,
} from "@fronterahealth/frontera-ui-components";

import { SuggestionIndicator } from "@components/QaReview/components/SuggestionIndicator";
import { useFieldReview } from "@components/QaReview/hooks/useFieldReview";
import { useIsReviewPanelVisible } from "@components/QaReview/hooks/useIsReviewPanelVisible";

type SuggestionProps =
  | { suggestionFieldId: string; suggestionCount: number }
  | { suggestionFieldId?: never; suggestionCount?: never };

interface InlineEditFieldBaseProps<T extends Record<string, unknown>> {
  value: string | null | undefined;
  fieldKey: keyof T;
  onSave: (fields: Partial<T>) => Promise<{ success: boolean; error?: string }>;
  title: string;
  isMultiline?: boolean;
  required?: boolean;
  inputType?: "text" | "date" | "select";
  selectOptions?: SelectItem[];
  isReportCompleted?: boolean;
  isReadOnly?: boolean;
  transformBeforeSave?: (value: string) => T[keyof T];
  valueVariant?: "paragraph" | "headline";
}

type InlineEditFieldProps<T extends Record<string, unknown>> = InlineEditFieldBaseProps<T> & SuggestionProps;

export const InlineEditField = <T extends Record<string, unknown>>({
  suggestionFieldId,
  suggestionCount = 0,
  value,
  fieldKey,
  onSave,
  title,
  isMultiline = false,
  required = false,
  inputType = "text",
  selectOptions = [],
  isReportCompleted = false,
  isReadOnly = false,
  transformBeforeSave,
  valueVariant = "paragraph",
}: InlineEditFieldProps<T>) => {
  const { isActive, activate, deactivate } = useFieldReview();
  const isReviewPanelVisible = useIsReviewPanelVisible();

  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [inputValue, setInputValue] = useState(value === "-" ? "" : value || "");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const selectContainerRef = useRef<HTMLDivElement>(null);
  const isDropdownOpenRef = useRef(false);

  // Only sync from the prop when it genuinely changes externally, not when
  // re-rendering after our own save — otherwise the optimistic selection
  // briefly reverts to the stale prop before the parent re-render lands.
  const lastSyncedValueRef = useRef(value);

  // Find the selected item for select type
  const getSelectedItem = useCallback(() => {
    if (inputType !== "select" || !selectOptions.length) return undefined;
    return selectOptions.find((opt) => opt.id === value) || undefined;
  }, [inputType, selectOptions, value]);

  const [selectedItem, setSelectedItem] = useState<SelectItem | undefined>(getSelectedItem());

  useEffect(() => {
    if (isEditing) return;
    if (value === lastSyncedValueRef.current) return;
    lastSyncedValueRef.current = value;

    if (inputType === "select") {
      const foundItem = selectOptions.find((opt) => opt.id === value) || undefined;
      setSelectedItem(foundItem);
      setInputValue(foundItem?.primary || "");
    } else {
      setInputValue(value === "-" ? "" : value || "");
    }
  }, [value, inputType, selectOptions, isEditing]);
  const isFocused = isActive(suggestionFieldId);

  // Helper function to focus the appropriate input element (primarily for Select components)
  const focusInputElement = useCallback(() => {
    if (inputType === "select" && selectContainerRef.current) {
      // For Headless UI combobox buttons, look for button with aria-haspopup="listbox"
      // Also check for standard combobox role as fallback
      const selectInput =
        (selectContainerRef.current.querySelector(
          'button[aria-haspopup="listbox"], button[role="combobox"], input',
        ) as HTMLElement) || null;
      if (selectInput) {
        selectInput.focus();
        selectInput.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        // Fallback: scroll the container into view
        selectContainerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
    // Note: Input/TextArea use autoFocus prop instead for better focus ring support
  }, [inputType]);

  // Auto-enter editing mode when field is focused via suggestion activation
  useEffect(() => {
    if (isFocused && suggestionFieldId && !isReportCompleted && !isReadOnly) {
      setIsEditing(true);
      setError(null);
    }
  }, [isFocused, suggestionFieldId, isReportCompleted, isReadOnly]);

  // Focus Select input when entering editing mode - use useLayoutEffect for synchronous DOM access
  // Note: Input/TextArea use autoFocus prop for better focus ring support
  useLayoutEffect(() => {
    if (isEditing && !isReportCompleted && !isReadOnly && inputType === "select") {
      // useLayoutEffect runs synchronously after DOM mutations, so the element should exist
      // For Select components, we need to manually focus since they don't support autoFocus
      focusInputElement();
    }
  }, [isEditing, isReportCompleted, isReadOnly, inputType, focusInputElement]);

  // Handle click outside for select type
  const handleClick = () => {
    setIsEditing(true);
    setError(null);
    activate(suggestionFieldId);
  };

  const handleBlur = useCallback(async () => {
    // Validate required field
    if (required && inputValue.trim() === "") {
      setError(`${title} is required`);
      return;
    }

    setError(null);
    try {
      const valueToSave = transformBeforeSave ? transformBeforeSave(inputValue) : inputValue;
      const result = await onSave({ [fieldKey]: valueToSave } as Partial<T>);
      if (!result.success && result.error) {
        setError(result.error);
        return;
      }
      // Only exit editing mode after successful save to prevent useEffect from reverting inputValue
      setIsEditing(false);
      setIsHovered(false);
    } catch (err) {
      console.error("Error saving field:", err);
      setInputValue(value || "");
      setError("Failed to save changes");
      setIsEditing(false);
      setIsHovered(false);
    }
    deactivate(suggestionFieldId);
  }, [inputValue, value, onSave, fieldKey, required, title, transformBeforeSave, deactivate, suggestionFieldId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !isMultiline) {
        e.preventDefault();
        (e.target as HTMLInputElement).blur();
      }
      if (e.key === "Escape") {
        setInputValue(value || "");
        setIsEditing(false);
        setError(null);
      }
    },
    [isMultiline, value],
  );

  const handleSelectChange = useCallback(
    async (newSelected: SelectItem) => {
      setSelectedItem(newSelected);
      setInputValue(newSelected.primary);
      setError(null);
      try {
        const idValue = newSelected.id ?? newSelected.primary;
        const valueToSave = transformBeforeSave ? transformBeforeSave(idValue) : idValue;
        const result = await onSave({ [fieldKey]: valueToSave } as Partial<T>);
        if (!result.success && result.error) {
          setError(result.error);
        }
      } catch (err) {
        console.error("Error saving field:", err);
        setError("Failed to save changes");
      }
    },
    [onSave, fieldKey, transformBeforeSave],
  );

  const Title = ({ title }: { title: string }) => {
    return (
      <div className="flex flex-row items-center justify-between gap-x-2">
        <Small displayType="normal" colorType="secondary">
          {title}
        </Small>
        {suggestionCount > 0 && isReviewPanelVisible && suggestionFieldId && (
          <SuggestionIndicator fieldId={suggestionFieldId} suggestionCount={suggestionCount} />
        )}
      </div>
    );
  };
  const displayValue = inputType === "date" && inputValue ? formatDate(inputValue) : inputValue || "--";

  const renderEditor = () => {
    // Use autoFocus for non-select inputs to ensure focus ring appears
    const shouldAutoFocus = isEditing && !isReportCompleted && !isReadOnly && inputType !== "select";

    return (
      <div>
        {isMultiline ? (
          <TextArea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            label={""}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (error) setError(null);
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            rows={3}
            errorMessage={error || undefined}
            formNoValidate={!error}
            autoFocus={shouldAutoFocus}
          />
        ) : inputType === "select" ? (
          <div
            ref={selectContainerRef}
            onBlur={(e) => {
              // Trigger transiently blurs as the user clicks an option; ignore until the dropdown closes.
              if (isDropdownOpenRef.current) return;
              if (!selectContainerRef.current?.contains(e.relatedTarget as Node)) {
                setIsEditing(false);
                setIsHovered(false);
              }
            }}
          >
            <Select
              title=""
              items={selectOptions}
              placeholderText={`Select ${title}`}
              selected={selectedItem}
              setSelected={handleSelectChange}
              variant="report"
              inputClassName="rounded-lg!"
              formNoValidate={!error}
              errorMessage={error || undefined}
              onOpenChange={(open) => {
                isDropdownOpenRef.current = open;
                if (!open) {
                  setIsEditing(false);
                  setIsHovered(false);
                }
              }}
            />
          </div>
        ) : (
          <Input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            name={fieldKey as string}
            label={""}
            type={inputType === "date" ? "date" : "text"}
            max={inputType === "date" ? MAX_DATE : undefined}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (error) setError(null);
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            errorMessage={error || undefined}
            inputSize="full"
            formNoValidate={!error}
            inputClassName={
              valueVariant === "headline"
                ? "text-[22px]! font-semibold! leading-[32px]! rounded-lg! p-3! -mt-2"
                : "rounded-lg! h-[48px]"
            }
            autoFocus={shouldAutoFocus}
          />
        )}
      </div>
    );
  };
  const renderReadOnly = () => {
    if (valueVariant === "headline") {
      return (
        <Headline size="medium" colorType="primary" className="font-semibold wrap-break-word">
          {displayValue}
        </Headline>
      );
    }
    return (
      <Paragraph displayType="normal" colorType="primary" className="wrap-break-word">
        {displayValue}
      </Paragraph>
    );
  };
  const renderHoverable = () => {
    return (
      <div
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={twMerge(
          " py-2 rounded-lg cursor-pointer transition-all duration-150 wrap-break-word",
          isHovered && !isReadOnly ? "border border-limestone-200 px-3" : "border border-transparent",
        )}
      >
        {renderReadOnly()}
      </div>
    );
  };

  const isEditable = isEditing && !isReportCompleted && !isReadOnly;

  const renderContent = () => {
    if (isEditable) return renderEditor();
    if (isReportCompleted) return renderReadOnly();
    return renderHoverable();
  };

  return (
    <div className="flex-1 min-w-0">
      <Title title={title} />
      {renderContent()}
    </div>
  );
};
